import type { PatientDocumentValidationStatus } from "@/constants/Messages";
import api, { CacheTagType } from "@/store/api";
import { sortDateComparator } from "@/utils/date.util";
import { getPatientDocumentExternalIdFromFileName } from "@/utils/messages.util";

interface GetPatientDocumentsRequest {
  channelId: string;
  patientExternalId: string;
}

interface GetPatientDocumentsResponse {
  data: PatientDocument[];
}

interface GetPatientDocumentDownloadURLRequest {
  fileName: string;
  patientExternalId: string;
}

interface GetPatientDocumentDownloadURLResponse {
  attachmentDownloadUrl?: string;
  fileValidationStatus?: PatientDocumentValidationStatus;
  isValidFile?: boolean;
}

/**
 * Represents a request to get the upload URL for a patient's document.
 *
 * @interface GetPatientDocumentUploadURLRequest
 *
 * @property {string} channelId - Senbird Channel URL.
 * @property {string} documentExternalId - UUID generated for the document.
 * @property {string} fileName - The name of the file to be uploaded - Without the UUID.
 * @property {string} patientExternalId - The external ID of the patient to whom the document belongs.
 */
interface GetPatientDocumentUploadURLRequest {
  channelId: string;
  documentExternalId: string;
  fileName: string;
  patientExternalId: string;
}

interface GetPatientDocumentUploadURLResponse {
  data: {
    attachmentUploadUrl: string;
  };
}

const chatApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPatientDocumentDownloadURL: build.query<
      GetPatientDocumentDownloadURLResponse,
      GetPatientDocumentDownloadURLRequest
    >({
      query: ({ fileName, patientExternalId }) => {
        const encodedFileName = encodeURIComponent(fileName);
        const documentExternalId =
          getPatientDocumentExternalIdFromFileName(fileName);

        return {
          url: `/clinical-data/family/patient-document/${patientExternalId}/download-url/${encodedFileName}?documentExternalId=${documentExternalId}`,
        };
      },
    }),

    getPatientDocuments: build.query<
      PatientDocument[],
      GetPatientDocumentsRequest
    >({
      providesTags: [CacheTagType.PatientDocuments],
      query: ({ channelId, patientExternalId }) =>
        `/clinical-data/family/patient-document/${patientExternalId}/channel/${channelId}/files/v1`,
      transformResponse: (response: GetPatientDocumentsResponse) =>
        // eslint-disable-next-line max-params, sonarjs/no-misleading-array-reverse -- currently reported in SonarQube as unresolved
        response.data.sort((a, b) =>
          sortDateComparator({
            first: a.createdAt,
            isDescending: true,
            second: b.createdAt,
          }),
        ),
    }),
    getPatientDocumentUploadURL: build.mutation<
      GetPatientDocumentUploadURLResponse["data"],
      GetPatientDocumentUploadURLRequest
    >({
      invalidatesTags: [CacheTagType.PatientDocuments],
      query: ({
        channelId,
        documentExternalId,
        fileName,
        patientExternalId,
      }) => {
        const encodedFileName = encodeURIComponent(fileName);

        return {
          body: {
            channelId,
            documentExternalId,
            fileName: encodedFileName,
            patientExternalId,
          },
          method: "POST",
          url: "/clinical-data/family/patient-document",
        };
      },
      transformResponse: (response: GetPatientDocumentUploadURLResponse) =>
        response.data,
    }),
    getUserChatToken: build.query<SendBirdApiTokenResponse, void>({
      providesTags: [CacheTagType.SendbirdToken],
      query: () => "/chat/family/token/v1",
    }),
  }),
});

export const {
  useGetPatientDocumentsQuery,
  useGetPatientDocumentUploadURLMutation,
  useGetUserChatTokenQuery,
  useLazyGetPatientDocumentDownloadURLQuery,
  useLazyGetUserChatTokenQuery,
} = chatApi;
